import React, { useState } from 'react';
import {
    Activity,
    AlertCircle,
    Bell,
    CheckCircle2,
    Eye,
    FileText,
    MessageSquare,
    Pause,
    Play,
    Power,
    Repeat,
    Rocket,
    ShieldCheck,
    Sparkles,
    ToggleLeft,
    UserRound,
    Zap,
} from 'lucide-react';
import { JobEvent, ServiceStatusBadge, eventKindLabel, formatRelativeTime } from '../services/servicesShared';
import { formatDate } from '../utils/dateFormatter';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
    state_changed: Sparkles,
    action: Zap,
    artifact_published: FileText,
    asked_owner: MessageSquare,
    asked_steward: Bell,
    approval_requested: ShieldCheck,
    approval_decided: ShieldCheck,
    looped_in: UserRound,
    messaged: MessageSquare,
    blocked: Pause,
    unblocked: Play,
    steered: MessageSquare,
    guided: Bell,
    answered: CheckCircle2,
    routine_changed: Repeat,
    wake_started: Activity,
    wake_finished: CheckCircle2,
    wake_failed: AlertCircle,
    wake_scheduled: Activity,
    turned_on: Power,
    turned_off: Power,
    owner_changed: UserRound,
    shadow_action: Eye,
    shadow_message: Eye,
    mode_changed: ToggleLeft,
    restarted: Rocket,
    note: FileText,
};

const TONES: Record<string, string> = {
    shadow_action: 'text-purple-600 dark:text-purple-400',
    shadow_message: 'text-purple-600 dark:text-purple-400',
    mode_changed: 'text-purple-600 dark:text-purple-400',
    restarted: 'text-purple-600 dark:text-purple-400',
    wake_failed: 'text-red-600 dark:text-red-400',
    blocked: 'text-amber-600 dark:text-amber-400',
    asked_steward: 'text-amber-600 dark:text-amber-400',
    approval_requested: 'text-amber-600 dark:text-amber-400',
    state_changed: 'text-blue-600 dark:text-blue-400',
    artifact_published: 'text-blue-600 dark:text-blue-400',
    turned_off: 'text-muted-foreground',
};

const detailsText = (details: JobEvent['details']): string | null => {
    if (!details) return null;
    if (typeof details === 'string') return details.trim() || null;
    const keys = Object.keys(details);
    if (!keys.length) return null;
    return JSON.stringify(details, null, 2);
};

const EventRow: React.FC<{ event: JobEvent; onWake?: (wakeId: string) => void; rehearsal?: boolean }> = ({ event, onWake, rehearsal = false }) => {
    const [open, setOpen] = useState(false);
    const Icon = ICONS[event.kind] || Activity;
    const tone = TONES[event.kind] || 'text-muted-foreground';
    const details = detailsText(event.details);
    const expandable = Boolean(details || event.evidence?.length);
    return (
        <li className={`relative pl-8 pb-4 last:pb-0 ${rehearsal ? 'opacity-60' : ''}`}>
            <span className="absolute left-0 top-0.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background">
                <Icon className={`h-3 w-3 ${tone}`} />
            </span>
            <div className="flex items-start justify-between gap-3">
                <button
                    type="button"
                    className={`text-left text-sm min-w-0 ${expandable ? 'hover:underline' : 'cursor-default'}`}
                    onClick={() => expandable && setOpen((value) => !value)}
                >
                    {event.summary}
                </button>
                <span className="text-xs text-muted-foreground whitespace-nowrap" title={event.created_at ? formatDate(event.created_at) : undefined}>
                    {formatRelativeTime(event.created_at)}
                </span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                <span>{eventKindLabel(event.kind)}</span>
                {event.actor && event.actor !== 'job' && <span>· by {event.actor}</span>}
                {event.visibility === 'vendor' && <ServiceStatusBadge status="vendor" />}
                {event.visibility === 'owner' && <ServiceStatusBadge status="owner" />}
                {event.wake_id && onWake && (
                    <button type="button" className="underline-offset-2 hover:underline" onClick={() => onWake(event.wake_id!)}>
                        · wake
                    </button>
                )}
            </div>
            {open && (
                <div className="mt-2 space-y-2">
                    {details && (
                        <pre className="max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap break-all">
                            {details}
                        </pre>
                    )}
                    {event.evidence?.length ? (
                        <div className="text-xs text-muted-foreground">
                            Evidence: <span className="font-mono">{event.evidence.join(', ')}</span>
                        </div>
                    ) : null}
                </div>
            )}
        </li>
    );
};

/** The action log (job_events), newest first, with live rows appended by the stream. */
export const JobEventLog: React.FC<{
    events: JobEvent[];
    live: boolean;
    onWake?: (wakeId: string) => void;
    /** When set, entries before it are the shadow rehearsal: kept for review, not part of the job's history. */
    restartedAt?: string | null;
}> = ({ events, live, onWake, restartedAt }) => {
    const [showAll, setShowAll] = useState(false);
    const sorted = [...events].sort((a, b) => b.seq - a.seq);
    const visible = showAll ? sorted : sorted.slice(0, 40);
    const cutoff = restartedAt ? new Date(restartedAt).getTime() : null;
    const isRehearsal = (event: JobEvent) =>
        cutoff !== null && event.kind !== 'restarted' && Boolean(event.created_at) && new Date(event.created_at!).getTime() < cutoff;
    const firstRehearsalIndex = visible.findIndex(isRehearsal);
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Action log</h3>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={`h-1.5 w-1.5 rounded-full ${live ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground/40'}`} />
                    {live ? 'Live' : 'Reconnecting…'}
                </span>
            </div>
            {sorted.length ? (
                <ol className="relative border-l border-dashed border-border/70 ml-2.5 pl-0 [&>li]:-ml-[11px]">
                    {visible.map((event, index) => (
                        <React.Fragment key={event.id}>
                            {index === firstRehearsalIndex && (
                                <li className="relative pl-8 pb-4">
                                    <span className="absolute left-0 top-0.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background">
                                        <Eye className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                                    </span>
                                    <p className="text-xs font-medium text-purple-700 dark:text-purple-400">Rehearsal (shadow mode)</p>
                                    <p className="text-xs text-muted-foreground">
                                        Kept for review. The job started over when it went live, so nothing below is part of its history.
                                    </p>
                                </li>
                            )}
                            <EventRow event={event} onWake={onWake} rehearsal={isRehearsal(event)} />
                        </React.Fragment>
                    ))}
                </ol>
            ) : (
                <p className="text-sm text-muted-foreground">Nothing logged yet. The first wake writes the first entries.</p>
            )}
            {sorted.length > visible.length && (
                <button type="button" className="mt-3 text-xs text-muted-foreground hover:text-foreground underline" onClick={() => setShowAll(true)}>
                    Show all {sorted.length} entries
                </button>
            )}
        </div>
    );
};

export default JobEventLog;
